import { action, set, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { Hotel, Market, User } from 'frontend-utils/models';
import type HotelSettingsHotelsController from 'lumen/controllers/hotel/settings/hotels';
import type { Transition } from 'lumen/services/transition';

type Model = {
  isLoading?: boolean;
  page: number;
  totalPages: number;
  totalResults: number;
  users: User[];
};

type Params = {
  hotelId: string;
  marketId: string;
  page: number;
  user: string;
};

export default class HotelSettingsHotelsRoute extends Route {
  queryParams = {
    hotelId: {
      refreshModel: true,
    },
    marketId: {
      refreshModel: true,
    },
    page: {
      refreshModel: true,
    },
    refresh: {
      refreshModel: true,
    },
    user: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  fetchOptions = true;

  activate(): void {
    this.fetchOptions = true;
  }

  beforeModel(transition: Transition): void {
    if (!this.access.hasFeature('settings.hotel_access.edit')) {
      this.transition.redirectToTab(transition, 'hotel.settings.index');
    } else {
      this.transition.validateHotelSettingsQueryParams(transition);
    }
  }

  /**
   * Necessary for fetching the selected user, hotel and/or market.
   * These are not present when visiting the route for the first time
   * @param  { object } model  current model
   * @param  { object } params  passes query params
   */

  fetchRecipientHotelMarket(model: Model, params: Params): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor(
      'hotel.settings.hotels',
    ) as HotelSettingsHotelsController;

    if (params.user) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store.findRecord<User>('user', params.user).then((user) => {
        if (user) {
          set(controller, 'selectedUser', {
            id: user.id,
            email: user.email,
          });
        } else {
          set(controller, 'selectedUser', {
            email: this.intl.t('settings.all_users_filter'),
          });
        }
      });
    }

    if (params.hotelId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store.findRecord<Hotel>('hotel', params.hotelId).then((hotel) => {
        if (hotel) {
          set(controller, 'selectedHotelOrMarket', {
            id: hotel.id,
            label: hotel.hotelinfo?.name,
          });
        } else {
          set(controller, 'selectedHotelOrMarket', {
            label: this.intl.t('reports.all_hotels_markets_filters'),
          });
        }
      });
    }

    if (params.marketId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.store
        .findRecord<Market>('market', params.marketId)
        .then((market) => {
          if (market) {
            set(controller, 'selectedHotelOrMarket', {
              id: market.id,
              label: market.name,
            });
          } else {
            set(controller, 'selectedHotelOrMarket', {
              label: this.intl.t('reports.all_hotels_markets_filters'),
            });
          }
        });
    }

    set(this, 'fetchOptions', false);
  }

  model(params: Params): Model {
    const model = {
      isLoading: true,
      page: 0,
      totalPages: 0,
      totalResults: 0,
      users: [],
    };

    // no only_self here since we need the complete list
    this.store
      .query<User>('user', {
        user_id: params.user,
        hotel_id: params.hotelId,
        market_id: params.marketId,
        page: params.page - 1,
      })
      .then(
        (content: unknown) => {
          setProperties(model, {
            isLoading: false,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            totalPages: content.meta.results_num_pages + 1,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            totalResults: content.meta.results_count,
            // @ts-expect-error: Incorrect type
            users: [...model.users, ...content],
          });

          // Can only be done after the route is visited for the first time
          if (this.fetchOptions) {
            this.fetchRecipientHotelMarket(model, params);
          }
        },
        (error) => {
          if (
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-member-access
            error?.errors &&
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-member-access
            error.errors[0] &&
            // eslint-disable-next-line  @typescript-eslint/no-unsafe-member-access
            error.errors[0].code === 'page'
          ) {
            this.router.transitionTo({
              queryParams: {
                user: params.user,
                hotelId: params.hotelId,
                marketId: params.marketId,
                page: 1,
              },
            });
          } else {
            throw error;
          }
        },
      );

    return model;
  }

  resetController(
    controller: HotelSettingsHotelsController,
    isExiting?: boolean,
  ): void {
    if (isExiting) {
      // isExiting would be false if only the route's model was changing
      set(controller, 'page', 1);

      // Unload store
      this.store.unloadAll('userproductaccess');
      this.store.peekAll<User>('user').forEach((user) => {
        if (user !== this.hotelAndMarketService.user) {
          this.store.unloadRecord(user);
        }
      });
    }
  }

  setupController(controller: HotelSettingsHotelsController): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super.setupController(...arguments);

    if (controller.refresh) {
      set(controller, 'refresh', false);
    }
  }

  @action refreshRoute(): void {
    this.refresh();
  }
}
